/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import type { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Strikethrough,
  Code as CodeIcon,
  Highlighter,
  Link as LinkIcon,
  Heading as HeadingIcon,
  List as ListIcon,
  ListOrdered,
  Quote,
  SquareCode,
  Minus,
  Eraser,
  Check,
  Trash2,
} from 'lucide-react';
import type { CRMUser, CommentMention } from '../../types';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import {
  buildStructureMentionToken,
  filterStructuresForQuery,
} from '../../utils/proposalStructureMentions';
import { useAuth } from '../../utils/AuthContext';
import { mentionAffiliationLabel } from '../../utils/mentionAffiliation';
import {
  htmlToPlainWithMentions,
  isHtmlEmpty,
  sanitizeCommentHtml,
  uploadCommentInlineImage,
} from '../../utils/richComment';
import {
  insertOrToggleBlockquote,
  insertOrToggleCodeBlock,
  toggleBlockFormatOnSelection,
} from '../../utils/richCommentBlockFormat';
import {
  commentListExtensions,
  commentListStarterKitOptions,
  handleCommentListKeyDown,
} from '../../utils/richCommentListEditor';
import {
  TEAM_MENTION_ID,
  buildMentionSuggestionItems,
  type MentionSuggestionItem,
} from '../../utils/teamMention';
import {
  type HubHashTag,
  HUB_HASH_TAGS,
  filterHubHashMentionEntries,
  hubHashEntry,
  hubHashToken,
  isHubHashTag,
} from '../../utils/commentHubHashMentions';

export interface RichCommentEditorPayload {
  bodyHtml: string;
  bodyPlain: string;
  mentions: CommentMention[];
  isEmpty: boolean;
}

export interface RichCommentEditorHandle {
  /** Read the current editor state into a Firestore-ready payload (HTML + plain + mentions). */
  getPayload(): RichCommentEditorPayload;
  focus(): void;
  clear(): void;
  setHTML(html: string): void;
}

interface RichCommentEditorProps {
  initialHtml?: string;
  /** Used by the @mention suggestion. */
  mentionableUsers: CRMUser[];
  /**
   * Optional map of supplierOrgId → company name so supplier @mentions can show
   * affiliation when `users.company` is empty.
   */
  supplierOrgNameById?: Map<string, string>;
  /** Used by paste-image upload to scope the Storage path. */
  taskId: string;
  /** Fired on every doc change with the latest HTML; useful for draft autosave. */
  onChange?: (html: string) => void;
  /** Cmd/Ctrl+Enter shortcut handler. Receives `getPayload()` to avoid a re-render race. */
  onSubmit?: (payload: RichCommentEditorPayload) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** When true, render only the editor surface (no toolbar). Used for the inline-edit affordance to keep things compact. */
  hideToolbar?: boolean;
  /** When true, attach an explicit input handler that uploads pasted images. Defaults to true. */
  enablePasteImage?: boolean;
  /** Project comments only: offer @team to notify everyone in the thread channel. */
  enableTeamMention?: boolean;
  /** Architect Discussion: pin architects, designers, and engineers at the top of @. */
  mentionPrioritizePartners?: boolean;
  assignedArchitectUids?: string[];
  /** `#board`, `#tl`, `#files`, `#COs` autocomplete — omit to offer all tags. */
  hubHashMentionIds?: readonly HubHashTag[];
  /** Project Hub: `/` inserts a proposal structure mention for this deal. */
  structureMentionDealId?: string;
  structureMentions?: StructureRef[];
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Mention suggestion popover                                                */
/*  Rendered as a vanilla DOM popover anchored to the caret rect.            */
/*  We wrap it in a vanilla popover (instead of React/tippy) to avoid having  */
/*  to chain a portal through TipTap's suggestion lifecycle.                  */
/* ────────────────────────────────────────────────────────────────────────── */

export const CommentBoardMentionExtension = Mention.extend({
  name: 'hubHashMention',
  parseHTML() {
    return [
      {
        tag: 'span[data-hub-link]',
        getAttrs: element => {
          const id = (element as HTMLElement).getAttribute('data-hub-link') || '';
          if (!id) return false;
          return { id, label: id };
        },
      },
      {
        tag: 'span[data-board]',
        getAttrs: () => ({ id: 'board', label: 'board' }),
      },
      {
        tag: 'span.rce-board-mention',
        getAttrs: () => ({ id: 'board', label: 'board' }),
      },
      {
        tag: 'span.rce-hub-hash-mention',
        getAttrs: element => {
          const id = (element as HTMLElement).getAttribute('data-hub-link') || '';
          if (!id) return false;
          return { id, label: id };
        },
      },
    ];
  },
});

type HubHashSelection = { id: string; label: string };

export const CommentStructureMentionExtension = Mention.extend({
  name: 'structureMention',
  parseHTML() {
    return [
      {
        tag: 'span[data-structure-id][data-deal-id]',
        getAttrs: (element) => {
          const el = element as HTMLElement;
          const structureId = el.getAttribute('data-structure-id') || '';
          const dealId = el.getAttribute('data-deal-id') || '';
          if (!structureId || !dealId) return false;
          const label = el.getAttribute('data-label') || (el.textContent ?? '').trim() || 'Structure';
          return { id: `${dealId}::${structureId}`, label };
        },
      },
      {
        tag: 'span.rce-structure-mention',
        getAttrs: (element) => {
          const el = element as HTMLElement;
          const structureId = el.getAttribute('data-structure-id') || '';
          const dealId = el.getAttribute('data-deal-id') || '';
          if (!structureId || !dealId) return false;
          const label = el.getAttribute('data-label') || (el.textContent ?? '').trim() || 'Structure';
          return { id: `${dealId}::${structureId}`, label };
        },
      },
    ];
  },
});

type StructureMentionSelection = { id: string; label: string };

function parseStructureMentionNodeId(raw: string): { dealId: string; structureId: string } | null {
  const idx = raw.indexOf('::');
  if (idx <= 0) return null;
  const dealId = raw.slice(0, idx).trim();
  const structureId = raw.slice(idx + 2).trim();
  if (!dealId || !structureId) return null;
  return { dealId, structureId };
}

export function buildStructureMentionSuggestion(
  getDealId: () => string,
  getStructures: () => StructureRef[],
) {
  return {
    char: '/',
    items: ({ query }: { query: string }) => {
      const dealId = getDealId();
      if (!dealId) return [];
      return filterStructuresForQuery(getStructures(), query).map((s) => ({
        id: `${dealId}::${s.id}`,
        label: s.name,
      }));
    },
    render: () => {
      let container: HTMLDivElement | null = null;
      let selectedIndex = 0;
      let currentItems: StructureMentionSelection[] = [];
      let currentCommand: ((p: StructureMentionSelection) => void) | null = null;

      const renderList = () => {
        if (!container) return;
        container.innerHTML = '';
        if (currentItems.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'rce-mention-empty';
          empty.textContent = 'No structures';
          container.appendChild(empty);
          return;
        }
        currentItems.forEach((item, i) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = `rce-mention-option${i === selectedIndex ? ' active' : ''}`;
          const name = document.createElement('span');
          name.className = 'rce-mention-option-name';
          name.textContent = item.label;
          btn.appendChild(name);
          btn.onmousedown = (e) => {
            e.preventDefault();
            currentCommand?.(item);
          };
          container!.appendChild(btn);
        });
      };

      const positionAt = (rect: DOMRect | null | undefined) => {
        if (!container || !rect) return;
        const margin = 6;
        const ph = container.offsetHeight || 80;
        let top = rect.bottom + margin;
        if (top + ph > window.innerHeight - margin) {
          top = Math.max(margin, rect.top - ph - margin);
        }
        const pw = container.offsetWidth || 160;
        let left = rect.left;
        if (left + pw > window.innerWidth - margin) {
          left = Math.max(margin, window.innerWidth - pw - margin);
        }
        container.style.top = `${top}px`;
        container.style.left = `${left}px`;
      };

      return {
        onStart: (props: SuggestionProps<StructureMentionSelection, StructureMentionSelection>) => {
          container = document.createElement('div');
          container.className = 'rce-mention-popover';
          container.setAttribute('role', 'listbox');
          document.body.appendChild(container);
          currentItems = props.items;
          currentCommand = props.command;
          selectedIndex = 0;
          renderList();
          positionAt(props.clientRect?.());
        },
        onUpdate: (props: SuggestionProps<StructureMentionSelection, StructureMentionSelection>) => {
          currentItems = props.items;
          currentCommand = props.command;
          if (selectedIndex >= currentItems.length) selectedIndex = 0;
          renderList();
          positionAt(props.clientRect?.());
        },
        onKeyDown: (props: SuggestionKeyDownProps) => {
          const e = props.event;
          if (!e) return false;
          if (e.key === 'ArrowDown') {
            selectedIndex = (selectedIndex + 1) % Math.max(currentItems.length, 1);
            renderList();
            return true;
          }
          if (e.key === 'ArrowUp') {
            selectedIndex = (selectedIndex - 1 + currentItems.length) % Math.max(currentItems.length, 1);
            renderList();
            return true;
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            const item = currentItems[selectedIndex];
            if (item) {
              e.preventDefault();
              currentCommand?.(item);
              return true;
            }
          }
          if (e.key === 'Escape') {
            container?.remove();
            container = null;
            return true;
          }
          return false;
        },
        onExit: () => {
          container?.remove();
          container = null;
        },
      };
    },
  };
}

export function buildBoardMentionSuggestion(getAllowedIds: () => readonly HubHashTag[] = () => HUB_HASH_TAGS) {
  return {
    char: '#',
    items: ({ query }: { query: string }) => {
      const entries = filterHubHashMentionEntries(getAllowedIds(), query);
      return entries.map(entry => ({ id: entry.id, label: entry.id }));
    },
    render: () => {
      let container: HTMLDivElement | null = null;
      let selectedIndex = 0;
      let currentItems: HubHashSelection[] = [];
      let currentCommand: ((p: HubHashSelection) => void) | null = null;

      const renderList = () => {
        if (!container) return;
        container.innerHTML = '';
        if (currentItems.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'rce-mention-empty';
          empty.textContent = 'No matches';
          container.appendChild(empty);
          return;
        }
        currentItems.forEach((item, i) => {
          const entry = hubHashEntry(item.id as HubHashTag);
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = `rce-mention-option${i === selectedIndex ? ' active' : ''}`;
          const name = document.createElement('span');
          name.className = 'rce-mention-option-name';
          name.textContent = entry.menuLabel;
          btn.appendChild(name);
          const chip = document.createElement('span');
          chip.className = 'rce-mention-option-affil';
          chip.textContent = entry.token;
          btn.appendChild(chip);
          btn.onmousedown = e => {
            e.preventDefault();
            currentCommand?.(item);
          };
          container!.appendChild(btn);
          // The list scrolls once `#files-…` folder entries are included.
          if (i === selectedIndex) btn.scrollIntoView({ block: 'nearest' });
        });
      };

      const positionAt = (rect: DOMRect | null | undefined) => {
        if (!container || !rect) return;
        const margin = 6;
        const ph = container.offsetHeight || 80;
        let top = rect.bottom + margin;
        if (top + ph > window.innerHeight - margin) {
          top = Math.max(margin, rect.top - ph - margin);
        }
        const pw = container.offsetWidth || 160;
        let left = rect.left;
        if (left + pw > window.innerWidth - margin) {
          left = Math.max(margin, window.innerWidth - pw - margin);
        }
        container.style.top = `${top}px`;
        container.style.left = `${left}px`;
      };

      return {
        onStart: (props: SuggestionProps<HubHashSelection, HubHashSelection>) => {
          container = document.createElement('div');
          container.className = 'rce-mention-popover';
          container.setAttribute('role', 'listbox');
          document.body.appendChild(container);
          currentItems = props.items;
          currentCommand = props.command;
          selectedIndex = 0;
          renderList();
          positionAt(props.clientRect?.());
        },
        onUpdate: (props: SuggestionProps<HubHashSelection, HubHashSelection>) => {
          currentItems = props.items;
          currentCommand = props.command;
          if (selectedIndex >= currentItems.length) selectedIndex = 0;
          renderList();
          positionAt(props.clientRect?.());
        },
        onKeyDown: (props: SuggestionKeyDownProps) => {
          const e = props.event;
          if (!e) return false;
          if (e.key === 'ArrowDown') {
            selectedIndex = (selectedIndex + 1) % Math.max(currentItems.length, 1);
            renderList();
            return true;
          }
          if (e.key === 'ArrowUp') {
            selectedIndex = (selectedIndex - 1 + currentItems.length) % Math.max(currentItems.length, 1);
            renderList();
            return true;
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            const item = currentItems[selectedIndex];
            if (item) {
              e.preventDefault();
              currentCommand?.(item);
              return true;
            }
          }
          if (e.key === 'Escape') {
            container?.remove();
            container = null;
            return true;
          }
          return false;
        },
        onExit: () => {
          container?.remove();
          container = null;
        },
      };
    },
  };
}

export const CommentMentionExtension = Mention.extend({
  parseHTML() {
    return [
      {
        tag: 'span[data-mention]',
        getAttrs: (element) => {
          const el = element as HTMLElement;
          const id = el.getAttribute('data-user-id') || '';
          if (!id) return false;
          const label =
            el.getAttribute('data-label') || (el.textContent || '').replace(/^@/, '').trim() || 'user';
          return { id, label };
        },
      },
      {
        tag: 'span.rce-mention',
        getAttrs: (element) => {
          const el = element as HTMLElement;
          const id = el.getAttribute('data-user-id') || '';
          if (!id) return false;
          const label =
            el.getAttribute('data-label') || (el.textContent || '').replace(/^@/, '').trim() || 'user';
          return { id, label };
        },
      },
    ];
  },
});

type MentionSelection = { id: string; label: string };
type MentionStartProps = SuggestionProps<MentionSuggestionItem, MentionSelection>;

function mentionItemSelection(item: MentionSuggestionItem): MentionSelection {
  if (item.kind === 'team') return { id: TEAM_MENTION_ID, label: 'team' };
  const first = (item.user.displayName || '').trim().split(/\s+/)[0] || 'user';
  return { id: item.user.uid, label: first };
}

export function buildMentionSuggestion(
  getUsers: () => CRMUser[],
  getSupplierOrgNameById?: () => Map<string, string> | null | undefined,
  getViewerRole?: () => string | null | undefined,
  getEnableTeamMention?: () => boolean,
  getPrioritizePartners?: () => boolean,
  getAssignedArchitectUids?: () => string[],
) {
  return {
    char: '@',
    items: ({ query }: { query: string }) => buildMentionSuggestionItems(getUsers(), query, {
      enableTeamMention: !!getEnableTeamMention?.(),
      viewerRole: getViewerRole?.(),
      prioritizeDesignPartners: !!getPrioritizePartners?.(),
      assignedArchitectUids: getAssignedArchitectUids?.() || [],
    }),
    render: () => {
      let container: HTMLDivElement | null = null;
      let selectedIndex = 0;
      let currentItems: MentionSuggestionItem[] = [];
      let currentCommand: ((p: MentionSelection) => void) | null = null;

      const renderList = () => {
        if (!container) return;
        container.innerHTML = '';
        if (currentItems.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'rce-mention-empty';
          empty.textContent = 'No matches';
          container.appendChild(empty);
          return;
        }
        const orgMap = getSupplierOrgNameById?.() ?? null;
        const teamItems = currentItems.filter((item) => item.kind === 'team');
        const userItems = currentItems.filter((item): item is Extract<MentionSuggestionItem, { kind: 'user' }> => item.kind === 'user');
        const roleOf = (item: Extract<MentionSuggestionItem, { kind: 'user' }>) =>
          (item.user.role || '').toLowerCase();
        const architects = userItems.filter((item) => roleOf(item) === 'architect');
        const designers = userItems.filter((item) => roleOf(item) === 'designer');
        const engineers = userItems.filter((item) => roleOf(item) === 'engineer');
        const staff = userItems.filter((item) => !['architect', 'designer', 'engineer'].includes(roleOf(item)));
        currentItems = [...teamItems, ...architects, ...designers, ...engineers, ...staff];
        const peopleGroups = [
          { items: architects, label: 'Architect' },
          { items: designers, label: 'Designer' },
          { items: engineers, label: 'Engineer' },
          { items: staff, label: 'Team' },
        ].filter((g) => g.items.length > 0);
        const showPeopleHeaders = peopleGroups.length > 1;
        currentItems.forEach((item, i) => {
          if (item.kind === 'team') {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = `rce-mention-option rce-mention-option-team${i === selectedIndex ? ' active' : ''}`;
            const name = document.createElement('span');
            name.className = 'rce-mention-option-name';
            name.textContent = 'Team';
            btn.appendChild(name);
            const chip = document.createElement('span');
            chip.className = 'rce-mention-option-affil';
            chip.textContent = 'Everyone in this channel';
            btn.appendChild(chip);
            btn.onmousedown = (e) => {
              e.preventDefault();
              currentCommand?.(mentionItemSelection(item));
            };
            container!.appendChild(btn);
            return;
          }
          if (showPeopleHeaders) {
            for (let g = 0; g < peopleGroups.length; g++) {
              if (item === peopleGroups[g].items[0]) {
                const head = document.createElement('div');
                head.className = 'rce-mention-group';
                head.textContent = peopleGroups[g].label;
                container!.appendChild(head);
                break;
              }
            }
          }
          const u = item.user;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = `rce-mention-option${i === selectedIndex ? ' active' : ''}`;
          const name = document.createElement('span');
          name.className = 'rce-mention-option-name';
          name.textContent = u.displayName;
          btn.appendChild(name);
          const affiliation = mentionAffiliationLabel(u, orgMap);
          if (affiliation) {
            const chip = document.createElement('span');
            chip.className = 'rce-mention-option-affil';
            chip.textContent = affiliation;
            btn.appendChild(chip);
          }
          btn.onmousedown = (e) => {
            e.preventDefault();
            currentCommand?.(mentionItemSelection(item));
          };
          container!.appendChild(btn);
        });
      };

      const positionAt = (rect: DOMRect | null | undefined) => {
        if (!container || !rect) return;
        const margin = 6;
        const ph = container.offsetHeight || 200;
        let top = rect.bottom + margin;
        if (top + ph > window.innerHeight - margin) {
          top = Math.max(margin, rect.top - ph - margin);
        }
        const pw = container.offsetWidth || 220;
        let left = rect.left;
        if (left + pw > window.innerWidth - margin) {
          left = Math.max(margin, window.innerWidth - pw - margin);
        }
        container.style.top = `${top}px`;
        container.style.left = `${left}px`;
      };

      return {
        onStart: (props: MentionStartProps) => {
          container = document.createElement('div');
          container.className = 'rce-mention-popover';
          container.setAttribute('role', 'listbox');
          document.body.appendChild(container);
          currentItems = props.items;
          currentCommand = props.command;
          selectedIndex = 0;
          renderList();
          positionAt(props.clientRect?.());
        },
        onUpdate: (props: MentionStartProps) => {
          currentItems = props.items;
          currentCommand = props.command;
          if (selectedIndex >= currentItems.length) selectedIndex = 0;
          renderList();
          positionAt(props.clientRect?.());
        },
        onKeyDown: (props: SuggestionKeyDownProps) => {
          const e = props.event;
          if (!e) return false;
          if (e.key === 'ArrowDown') {
            selectedIndex = (selectedIndex + 1) % Math.max(currentItems.length, 1);
            renderList();
            return true;
          }
          if (e.key === 'ArrowUp') {
            selectedIndex = (selectedIndex - 1 + currentItems.length) % Math.max(currentItems.length, 1);
            renderList();
            return true;
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            const item = currentItems[selectedIndex];
            if (item) {
              e.preventDefault();
              currentCommand?.(mentionItemSelection(item));
              return true;
            }
          }
          if (e.key === 'Escape') {
            container?.remove();
            container = null;
            return true;
          }
          return false;
        },
        onExit: () => {
          container?.remove();
          container = null;
        },
      };
    },
  };
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Editor component                                                          */
/* ────────────────────────────────────────────────────────────────────────── */

const RichCommentEditor = React.forwardRef<RichCommentEditorHandle, RichCommentEditorProps>(
  function RichCommentEditor(
    {
      initialHtml = '',
      mentionableUsers,
      supplierOrgNameById,
      taskId,
      onChange,
      onSubmit,
      placeholder = 'Write a comment… (@ to mention, #board #tl #files #COs, ⌘/Ctrl-Enter to send)',
      hubHashMentionIds,
      structureMentionDealId,
      structureMentions,
      disabled = false,
      className,
      hideToolbar = false,
      enablePasteImage = true,
      enableTeamMention = false,
      mentionPrioritizePartners = false,
      assignedArchitectUids,
    },
    ref,
  ) {
    const { user } = useAuth();
    const usersRef = useRef<CRMUser[]>(mentionableUsers);
    usersRef.current = mentionableUsers;
    const supplierOrgNameByIdRef = useRef(supplierOrgNameById);
    supplierOrgNameByIdRef.current = supplierOrgNameById;
    const viewerRoleRef = useRef(user?.role);
    viewerRoleRef.current = user?.role;
    const enableTeamMentionRef = useRef(enableTeamMention);
    enableTeamMentionRef.current = enableTeamMention;
    const mentionPrioritizePartnersRef = useRef(mentionPrioritizePartners);
    mentionPrioritizePartnersRef.current = mentionPrioritizePartners;
    const assignedArchitectUidsRef = useRef(assignedArchitectUids || []);
    assignedArchitectUidsRef.current = assignedArchitectUids || [];
    const hubHashMentionIdsRef = useRef<readonly HubHashTag[]>(hubHashMentionIds ?? HUB_HASH_TAGS);
    hubHashMentionIdsRef.current = hubHashMentionIds ?? HUB_HASH_TAGS;
    const structureMentionDealIdRef = useRef(structureMentionDealId ?? '');
    structureMentionDealIdRef.current = structureMentionDealId ?? '';
    const structureMentionsRef = useRef(structureMentions ?? []);
    structureMentionsRef.current = structureMentions ?? [];

    const onSubmitRef = useRef<typeof onSubmit | null>(onSubmit ?? null);
    onSubmitRef.current = onSubmit ?? null;

    const taskIdRef = useRef(taskId);
    taskIdRef.current = taskId;

    const extensions = useMemo(
      () => [
        StarterKit.configure({
          heading: { levels: [3] },
          ...commentListStarterKitOptions,
          codeBlock: { HTMLAttributes: { class: 'rce-codeblock' } },
          blockquote: { HTMLAttributes: { class: 'rce-blockquote' } },
          // StarterKit v3 bundles Link — configure it here (do not add @tiptap/extension-link again).
          link: {
            openOnClick: false,
            autolink: true,
            linkOnPaste: true,
            HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank', class: 'rce-link' },
          },
        }),
        ...commentListExtensions(),
        Highlight.configure({ multicolor: false, HTMLAttributes: { class: 'rce-highlight' } }),
        Image.configure({ inline: false, HTMLAttributes: { class: 'rce-image' } }),
        Placeholder.configure({ placeholder }),
        CommentMentionExtension.configure({
          HTMLAttributes: { class: 'rce-mention', 'data-mention': 'true' },
          renderText({ node }) {
            const label = (node.attrs.label as string | null) || (node.attrs.id as string | null) || '';
            return `@${label}`;
          },
          renderHTML({ options, node }) {
            const id = (node.attrs.id as string | null) ?? '';
            const label = (node.attrs.label as string | null) || id || '';
            const isTeam = id === TEAM_MENTION_ID;
            const htmlAttrs: Record<string, string> = {
              ...(options.HTMLAttributes as Record<string, string>),
              'data-user-id': id,
              'data-label': label,
            };
            if (isTeam) {
              htmlAttrs.class = `${htmlAttrs.class || 'rce-mention'} rce-mention-team`.trim();
              htmlAttrs['data-team'] = 'true';
            }
            return ['span', htmlAttrs, `@${label}`];
          },
          suggestion: buildMentionSuggestion(
            () => usersRef.current,
            () => supplierOrgNameByIdRef.current,
            () => viewerRoleRef.current,
            () => enableTeamMentionRef.current,
            () => mentionPrioritizePartnersRef.current,
            () => assignedArchitectUidsRef.current,
          ),
        }),
        CommentBoardMentionExtension.configure({
          HTMLAttributes: { class: 'rce-hub-hash-mention' },
          renderText({ node }) {
            const raw = (node.attrs.id as string) || 'board';
            const id: HubHashTag = isHubHashTag(raw) ? raw : 'board';
            return hubHashToken(id);
          },
          renderHTML({ options, node }) {
            const raw = (node.attrs.id as string) || 'board';
            const id: HubHashTag = isHubHashTag(raw) ? raw : 'board';
            const token = hubHashToken(id);
            const htmlAttrs: Record<string, string> = {
              ...(options.HTMLAttributes as Record<string, string>),
              'data-hub-link': id,
              class: `rce-hub-hash-mention rce-hub-hash-mention--${id}${
                id === 'board' ? ' rce-board-mention' : ''
              }`.trim(),
            };
            if (id === 'board') htmlAttrs['data-board'] = '1';
            return ['span', htmlAttrs, token];
          },
          suggestion: buildBoardMentionSuggestion(() => hubHashMentionIdsRef.current),
        }),
        CommentStructureMentionExtension.configure({
          HTMLAttributes: { class: 'rce-structure-mention' },
          renderText({ node }) {
            const parsed = parseStructureMentionNodeId(String(node.attrs.id ?? ''));
            const label = (node.attrs.label as string) || 'Structure';
            if (!parsed) return label;
            return buildStructureMentionToken({
              dealId: parsed.dealId,
              structureId: parsed.structureId,
              name: label,
            });
          },
          renderHTML({ options, node }) {
            const parsed = parseStructureMentionNodeId(String(node.attrs.id ?? ''));
            const label = (node.attrs.label as string) || 'Structure';
            if (!parsed) {
              return ['span', { ...(options.HTMLAttributes as Record<string, string>) }, label];
            }
            return [
              'span',
              {
                ...(options.HTMLAttributes as Record<string, string>),
                'data-structure-id': parsed.structureId,
                'data-deal-id': parsed.dealId,
                'data-label': label,
                class: 'rce-structure-mention',
              },
              label,
            ];
          },
          suggestion: buildStructureMentionSuggestion(
            () => structureMentionDealIdRef.current,
            () => structureMentionsRef.current,
          ),
        }),
      ],
      // placeholder is closed-over by the configure() call; deps intentionally minimal.
      [placeholder],
    );

    const editor: Editor | null = useEditor({
      extensions,
      content: initialHtml || '',
      editable: !disabled,
      autofocus: false,
      editorProps: {
        attributes: {
          class: 'rce-content',
          'aria-label': 'Comment editor',
        },
        handleKeyDown(_view, event) {
          const ed = editor;
          if (!ed) return false;
          if (handleCommentListKeyDown(ed, event)) return true;
          if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === 'b') {
            event.preventDefault();
            insertOrToggleBlockquote(ed);
            return true;
          }
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            const html = ed.getHTML();
            const sanitized = sanitizeCommentHtml(html);
            const { plain, mentions } = htmlToPlainWithMentions(sanitized);
            onSubmitRef.current?.({
              bodyHtml: sanitized,
              bodyPlain: plain,
              mentions,
              isEmpty: isHtmlEmpty(sanitized),
            });
            event.preventDefault();
            return true;
          }
          return false;
        },
        handlePaste(view, event) {
          if (!enablePasteImage) return false;
          const items = event.clipboardData?.items;
          if (!items || items.length === 0) return false;
          const imageItem = Array.from(items).find((it) => it.type.startsWith('image/'));
          if (!imageItem) return false;
          const file = imageItem.getAsFile();
          if (!file) return false;
          event.preventDefault();
          (async () => {
            try {
              const uploaded = await uploadCommentInlineImage(file, taskIdRef.current);
              const ed = editor;
              if (!ed) return;
              ed.chain().focus().setImage({ src: uploaded.url, alt: uploaded.name }).run();
            } catch (err) {
              console.error('[RichCommentEditor] Paste-image upload failed:', err);
              const message = err instanceof Error ? err.message : String(err);
              window.alert(`Image upload failed: ${message}`);
            }
          })();
          return true;
        },
      },
      onUpdate: ({ editor: ed }) => {
        onChange?.(ed.getHTML());
      },
    });

    useEffect(() => {
      if (!editor) return;
      editor.setEditable(!disabled);
    }, [disabled, editor]);

    // If `initialHtml` changes externally (e.g. parent restores a draft), sync editor content
    // when it differs from the editor's current HTML to avoid resetting on every keystroke.
    useEffect(() => {
      if (!editor) return;
      const current = editor.getHTML();
      if ((initialHtml || '') !== current) {
        editor.commands.setContent(initialHtml || '', { emitUpdate: false });
      }
      // Only run when initialHtml prop identity changes (caller controls when to push).
    }, [initialHtml, editor]);

    useImperativeHandle(
      ref,
      (): RichCommentEditorHandle => ({
        getPayload(): RichCommentEditorPayload {
          if (!editor) {
            return { bodyHtml: '', bodyPlain: '', mentions: [], isEmpty: true };
          }
          const html = editor.getHTML();
          const sanitized = sanitizeCommentHtml(html);
          const { plain, mentions } = htmlToPlainWithMentions(sanitized);
          return {
            bodyHtml: sanitized,
            bodyPlain: plain,
            mentions,
            isEmpty: isHtmlEmpty(sanitized),
          };
        },
        focus() {
          editor?.commands.focus();
        },
        clear() {
          editor?.commands.clearContent(false);
        },
        setHTML(html: string) {
          editor?.commands.setContent(html || '', { emitUpdate: false });
        },
      }),
      [editor],
    );

    return (
      <div className={`rce-root${className ? ` ${className}` : ''}${disabled ? ' rce-root--disabled' : ''}`}>
        {!hideToolbar && <RichToolbar editor={editor} />}
        <EditorContent editor={editor} />
      </div>
    );
  },
);

export default RichCommentEditor;

/* ────────────────────────────────────────────────────────────────────────── */
/*  Toolbar                                                                   */
/* ────────────────────────────────────────────────────────────────────────── */

interface ToolbarBtnProps {
  editor: Editor | null;
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  /** Forwarded to the underlying button — used to anchor popovers (e.g. link). */
  buttonRef?: React.Ref<HTMLButtonElement>;
}

const ToolbarBtn: React.FC<ToolbarBtnProps> = ({ editor, active, onClick, title, children, buttonRef }) => (
  <button
    ref={buttonRef}
    type="button"
    className={`rce-tb-btn${active ? ' rce-tb-btn--active' : ''}`}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    aria-label={title}
    disabled={!editor}
  >
    {children}
  </button>
);

/**
 * Insert a brand-new bullet/ordered list at the cursor instead of wrapping the entire
 * paragraph (TipTap's default `toggleBulletList()` behaviour). Behaviour:
 *   - Active selection or cursor already inside a list → defer to TipTap's toggle (so
 *     "select 3 lines, click list" still works, and clicking again removes the list).
 *   - Cursor inside an empty paragraph → toggle that paragraph (matches user intent of
 *     "I'm starting a new list").
 *   - Cursor inside a non-empty paragraph → append a fresh empty list block immediately
 *     after the current paragraph, place the caret in its first item ready to type.
 */
function insertOrToggleList(editor: Editor, kind: 'bullet' | 'ordered') {
  const { from, to } = editor.state.selection;
  const hasSelection = from !== to;
  const command = kind === 'bullet' ? 'toggleBulletList' : 'toggleOrderedList';

  if (hasSelection) {
    toggleBlockFormatOnSelection(editor, () => {
      editor.chain().focus()[command]().run();
    });
    return;
  }

  if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
    editor.chain().focus()[command]().run();
    return;
  }

  const $pos = editor.state.doc.resolve(from);
  const currentBlock = $pos.parent;
  const isEmptyBlock = currentBlock.content.size === 0;

  if (isEmptyBlock) {
    editor.chain().focus()[command]().run();
    return;
  }

  const listType = kind === 'bullet' ? 'bulletList' : 'orderedList';
  const insertAt = $pos.after();
  editor
    .chain()
    .focus()
    .insertContentAt(insertAt, {
      type: listType,
      content: [{ type: 'listItem', content: [{ type: 'paragraph' }] }],
    })
    .focus(insertAt + 3)
    .run();
}

interface LinkPopoverProps {
  editor: Editor;
  anchor: HTMLElement;
  onClose: () => void;
}

const LinkPopover: React.FC<LinkPopoverProps> = ({ editor, anchor, onClose }) => {
  const popRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const initial = (editor.getAttributes('link').href as string | undefined) || '';
  const [url, setUrl] = useState(initial);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const reposition = useCallback(() => {
    const a = anchor.getBoundingClientRect();
    const pop = popRef.current;
    const ph = pop?.offsetHeight ?? 84;
    const pw = pop?.offsetWidth ?? 280;
    const margin = 8;
    let top = a.bottom + 6;
    if (top + ph > window.innerHeight - margin) {
      top = Math.max(margin, a.top - ph - 6);
    }
    let left = a.left;
    if (left + pw > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - pw - margin);
    }
    setPos({ top, left });
  }, [anchor]);

  useEffect(() => {
    reposition();
    const id = window.requestAnimationFrame(() => {
      reposition();
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(id);
  }, [reposition]);

  useEffect(() => {
    const onResize = () => reposition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [reposition]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target)) return;
      if (anchor.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  const apply = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      onClose();
      return;
    }
    let normalized = trimmed;
    if (!/^https?:\/\//i.test(normalized) && !/^mailto:/i.test(normalized)) {
      normalized = `https://${normalized}`;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
    onClose();
  };

  const remove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  return createPortal(
    <div
      ref={popRef}
      className="rce-link-popover"
      role="dialog"
      aria-label="Edit link"
      style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: 0, visibility: 'hidden' as const }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="text"
        value={url}
        placeholder="https://example.com"
        className="rce-link-input"
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            apply();
          }
        }}
      />
      <button type="button" className="rce-link-apply" onClick={apply} title="Apply link" aria-label="Apply link">
        <Check size={13} />
      </button>
      {initial && (
        <button type="button" className="rce-link-remove" onClick={remove} title="Remove link" aria-label="Remove link">
          <Trash2 size={13} />
        </button>
      )}
    </div>,
    document.body,
  );
};

const RichToolbar: React.FC<{ editor: Editor | null }> = ({ editor }) => {
  // Force a re-render when the editor selection changes so active-state styling tracks the caret.
  const [, setTick] = React.useState(0);
  const [linkOpen, setLinkOpen] = useState(false);
  const linkBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!editor) return;
    const fn = () => setTick((t) => (t + 1) % 1000);
    editor.on('selectionUpdate', fn);
    editor.on('transaction', fn);
    return () => {
      editor.off('selectionUpdate', fn);
      editor.off('transaction', fn);
    };
  }, [editor]);

  const openLinkPopover = () => {
    if (!editor) return;
    setLinkOpen((prev) => !prev);
  };

  return (
    <div className="rce-toolbar" role="toolbar" aria-label="Comment formatting">
      <ToolbarBtn editor={editor} title="Bold (⌘B)" active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()}>
        <BoldIcon size={13} />
      </ToolbarBtn>
      <ToolbarBtn editor={editor} title="Italic (⌘I)" active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()}>
        <ItalicIcon size={13} />
      </ToolbarBtn>
      <ToolbarBtn editor={editor} title="Strikethrough" active={editor?.isActive('strike')} onClick={() => editor?.chain().focus().toggleStrike().run()}>
        <Strikethrough size={13} />
      </ToolbarBtn>
      <ToolbarBtn editor={editor} title="Inline code" active={editor?.isActive('code')} onClick={() => editor?.chain().focus().toggleCode().run()}>
        <CodeIcon size={13} />
      </ToolbarBtn>
      <ToolbarBtn editor={editor} title="Highlight" active={editor?.isActive('highlight')} onClick={() => editor?.chain().focus().toggleHighlight().run()}>
        <Highlighter size={13} />
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        title="Insert / edit link"
        active={editor?.isActive('link') || linkOpen}
        onClick={openLinkPopover}
        buttonRef={linkBtnRef}
      >
        <LinkIcon size={13} />
      </ToolbarBtn>

      <span className="rce-tb-divider" aria-hidden />

      <ToolbarBtn editor={editor} title="Heading" active={editor?.isActive('heading', { level: 3 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}>
        <HeadingIcon size={13} />
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        title="Bulleted list"
        active={editor?.isActive('bulletList')}
        onClick={() => editor && insertOrToggleList(editor, 'bullet')}
      >
        <ListIcon size={13} />
      </ToolbarBtn>
      <ToolbarBtn
        editor={editor}
        title="Numbered list"
        active={editor?.isActive('orderedList')}
        onClick={() => editor && insertOrToggleList(editor, 'ordered')}
      >
        <ListOrdered size={13} />
      </ToolbarBtn>
      <ToolbarBtn editor={editor} title="Quote" active={editor?.isActive('blockquote')} onClick={() => insertOrToggleBlockquote(editor)}>
        <Quote size={13} />
      </ToolbarBtn>
      <ToolbarBtn editor={editor} title="Code block" active={editor?.isActive('codeBlock')} onClick={() => insertOrToggleCodeBlock(editor)}>
        <SquareCode size={13} />
      </ToolbarBtn>
      <ToolbarBtn editor={editor} title="Horizontal rule" onClick={() => editor?.chain().focus().setHorizontalRule().run()}>
        <Minus size={13} />
      </ToolbarBtn>

      <span className="rce-tb-divider" aria-hidden />

      <ToolbarBtn editor={editor} title="Clear formatting" onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()}>
        <Eraser size={13} />
      </ToolbarBtn>

      {linkOpen && editor && linkBtnRef.current && (
        <LinkPopover editor={editor} anchor={linkBtnRef.current} onClose={() => setLinkOpen(false)} />
      )}
    </div>
  );
};
